import React, {
  FC, useCallback, useEffect, useRef, useState, 
} from 'react';
import VideoContainer from '@/components/jumpDownloadApp/VideoContainer/video';
import PicContainer from '@/components/jumpDownloadApp/PicContainer/pic';
import {
  getOssImageUrl,
  setTitle,
  downLoadApp,
  getUrlParams,
  getVideoCoverUrl,
  preloadResult,
  getMockData,
} from '@/utils/utils';
import './index.less';
import {
  useShareData,
  useUserFeedDetail,
  useUserFeedData,
  useWxJsConfigSignature,
  requestGetSecondShareCard,
} from '@/models/appDownloadModel';
import { indexUBT } from '@/utils/pageUbt';
import { FromNativeOrWxParam, SHARE_WAYS } from '@/interface/downAppType';
import { WXSDK } from '@/utils/asyncImportJs';
import { DOWNLOAD_IMG } from '@/constants/config';
import Header from '@/components/jumpDownloadApp/header/header';
import appModel from '@/models/appModel';

const userAgent = navigator.userAgent.toLowerCase();

const DownLoadApp: FC<{}> = (props) => {
  const { queryShareData, shareData } = useShareData();
  const { queryUserFeedDetail, userFeedDetail } = useUserFeedDetail();
  const { queryUserFeedData, userFeedData } = useUserFeedData();
  const { wxJsConfigSignature, getWxJsConfigSignature } = useWxJsConfigSignature();
  const { querySecondShareCard, secondShareCard } = requestGetSecondShareCard();
  const stickyHeader = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const {
    shareType,
    businessId,
    catHouseName,
    shareForm,
    shareUserId,
    title,
    subTitle,
    autoJump,
  } = getUrlParams(
    (window.location.href || '').replace(/#\//g, ''),
  ) as unknown as FromNativeOrWxParam;

  const [pageStatus, setPageStatus] = useState<SHARE_WAYS>(
    +shareType || SHARE_WAYS.LIVING,
  );
  const [configData, setConfigData] = useState<any>(null);
  const domRef = useRef<HTMLDivElement>(null);
  const extraInfoRef = useRef<any>({});

  /**
   * 处理下载App的通用方法
   * 功能：根据当前的extraInfo数据构建下载参数并调用downLoadApp
   * 输入：可选的自定义参数覆盖默认值
   * 输出：执行下载App操作
   */
  const handleDownloadApp = useCallback((customOptions: {
    selector?: string;
    clipboardStr?: string;
    buttonName?: string;
    includeExtraInfo?: boolean;
  } = {}) => {
    const {
      selector = '',
      clipboardStr = '',
      buttonName = '',
      includeExtraInfo = true,
    } = customOptions;

    const { shareId = '' } = getUrlParams(window.location.href || '');

    const currentExtraInfo = extraInfoRef.current;
    
    // 构建下载参数
    const downloadOptions = {
      selector,
      clipboardStr,
      data: {},
    };

    // 如果需要包含额外信息，则添加data字段
    if (includeExtraInfo && currentExtraInfo?.sceneId) {
      // 用明确的条件拼接page参数，避免shareId为对象时报错
      const hasShareId = typeof shareId === 'string' && shareId.length > 0;
      const pageQueryArr = [];
      if (hasShareId) {
        pageQueryArr.push(`shareId=${encodeURIComponent(shareId)}`);
      }
      pageQueryArr.push('source=h5');
      const pageQuery = pageQueryArr.join('&');

      downloadOptions.data = {
        sceneSource: currentExtraInfo?.sceneId || 14,
        page: `${currentExtraInfo?.page}?${pageQuery}`,
        router: currentExtraInfo?.router,
        shareForm: currentExtraInfo?.shareForm,
        shareUserId: currentExtraInfo?.shareUserId,
      };
    }

    console.log('执行下载App操作，参数:', downloadOptions);
    
    // 执行下载
    downLoadApp(downloadOptions);

    // 埋点统计
    if (buttonName) {
      indexUBT.clickButton({
        businessInfo: {},
        buttonName,
      });
    }
  }, []);

  useEffect(() => {
    indexUBT.pageView({
      source: shareForm,
      uid: shareUserId,
      title,
      subtitle: subTitle,
    });

    // shareType: number; // 1 图片 2 视频 3 直播 4/0/undefined 其他（图片）
    // 分享场景 1 我的分享页 2 直播间分享 3 投喂分享
    if (pageStatus && pageStatus < SHARE_WAYS.LIVING) {
      queryUserFeedDetail({ userFeedId: businessId || '' });
      queryUserFeedData({ userFeedId: businessId || '' });
    } else if (pageStatus === SHARE_WAYS.LIVING) {
      queryShareData({
        type: pageStatus || 3,
        shareType: pageStatus || 3,
        catHouseId: businessId || '',
      });
    }

    getMockData().then((res) => {
      setConfigData(res);
    });
    
    if (userAgent?.indexOf('micromessenger') !== -1) {
      querySecondShareCard({ shareType, businessId });
      getWxJsConfigSignature();
    }

    // 初始化页面数据
    const initializePageData = async () => {
      try {
        // 获取分享信息的额外数据
        const shareInfoResult = await appModel.onShareInfoReady();
        
        const { extraInfo = {}, shareUserId } = shareInfoResult || {};
        
        extraInfoRef.current = { ...extraInfo, shareUserId };
      } catch (error) {
        console.error('初始化页面数据失败:', error);
        // 设置默认值，保证页面正常运行
        extraInfoRef.current = {};
      }
    };
    initializePageData();

    return () => {
      indexUBT.pageViewOut({
        source: shareForm,
        uid: shareUserId,
        title,
        subtitle: subTitle,
      });
      preloadResult.clear();
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      const isAutoJump = domRef.current && Number(autoJump) === 1;
      if (isAutoJump) {
        // 自动跳转下载页面
        handleDownloadApp({
          selector: '#footer-btn',
          buttonName: '', // 自动跳转不需要埋点
        });
      }
    }, 2000);
  }, [domRef.current, handleDownloadApp]);

  useEffect(() => {
    if (wxJsConfigSignature && secondShareCard) {
      const { nonceStr, signature, timestamp } = wxJsConfigSignature || {};

      const { shareTitle, shareSubTitle, imgUrl = '' } = secondShareCard || {};

      const cardImgUrlMap = {
        [SHARE_WAYS.PIC]: imgUrl,
        [SHARE_WAYS.VIDEO]: getVideoCoverUrl(imgUrl || ''),
        [SHARE_WAYS.LIVING]:
          getVideoCoverUrl(shareData?.vedioLink || '') || imgUrl,
        [SHARE_WAYS.OTHER]: imgUrl,
        [SHARE_WAYS.VIP_MGM]: imgUrl,
      };

      WXSDK().then((value) => {
        wx?.config({
          debug: false, // 开启调试模式,调用的所有api的返回值会在客户端alert出来，若要查看传入的参数，可以在pc端打开，参数信息会通过log打出，仅在pc端时才会打印。
          appId: 'wx4e9cc6da9aa26fa9', // 必填，公众号的唯一标识
          timestamp, // 必填，生成签名的时间戳
          nonceStr, // 必填，生成签名的随机串
          signature, // 必填，签名
          jsApiList: ['updateAppMessageShareData', 'updateTimelineShareData'], // 必填，需要使用的JS接口列表
          // openTagList: ['wx-open-launch-app'],
        });
        wx?.ready(() => {
          // config信息验证后会执行ready方法，所有接口调用都必须在config接口获得结果之后，config是一个客户端的异步操作，所以如果需要在页面加载时就调用相关接口，则须把相关接口放在ready函数中调用来确保正确执行。对于用户触发时才调用的接口，则可以直接调用，不需要放在ready函数中。
          wx.updateAppMessageShareData({
            title: shareTitle, // 分享标题
            desc: shareSubTitle, // 分享描述
            link: window.location.href || '', // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
            imgUrl: cardImgUrlMap[pageStatus] || DOWNLOAD_IMG.INTRO_VIDEO, // 分享图标
            success() {
              // 设置成功
              console.log('wxJsConfigSignature', wxJsConfigSignature);
            },
            cancel() {
              console.log('分享取消');
            },
          });
          wx.updateTimelineShareData({
            title: shareTitle, // 分享标题
            desc: shareSubTitle, // 分享描述
            link: window.location.href || '', // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
            imgUrl: cardImgUrlMap[pageStatus] || DOWNLOAD_IMG.INTRO_VIDEO, // 分享图标
            success() {
              // 设置成功
            },
            cancel() {
              console.log('分享取消');
            },
          });
        });
        wx?.error((res: any) => {
          // setIsReady(true);
          // config信息验证失败会执行error函数，如签名过期导致验证失败，具体错误信息可以打开config的debug模式查看，也可以在返回的res参数中查看，对于SPA可以在这里更新签名。
        });
      });
    }
    return () => {
      if (window?.isReady !== undefined) {
        delete window.isReady;
      }
    };
  }, [wxJsConfigSignature, secondShareCard]);

  useEffect(() => {
    if (shareData || catHouseName) {
      const { barName } = shareData || {};
      setTitle((catHouseName || barName || '街猫') as string);
    }
  }, [shareData, catHouseName]);

  const renderVideo = () => (
    <VideoContainer 
      shareData={shareData} 
      configData={configData} 
      onDownloadApp={handleDownloadApp}
    />
  );

  const renderPic = () => (
    <PicContainer
      userFeedDetail={userFeedDetail}
      userFeedData={userFeedData}
      configData={configData}
      onDownloadApp={handleDownloadApp}
    />
  );

  return (
    <div className="body" ref={content}>
      <div className="sticky-header" ref={stickyHeader}>
        <Header />
      </div>
      <div className="container">
        {pageStatus === SHARE_WAYS.LIVING ? renderVideo() : renderPic()}
        <div
          className="footer-btn"
          id="footer-btn"
          ref={domRef}
          onClick={() => handleDownloadApp({
            selector: '#footer-btn',
            buttonName: 'miaowa_h5_index_footer_dakaijiemao_cli',
          })}
        >
          <img
            className="footer-img"
            src={DOWNLOAD_IMG.INTRO_VIDEO}
          />
          App内打开
        </div>
      </div>
    </div>
  );
};
export default DownLoadApp;
